import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getIsStickyOtaMode, getProfileUrlParam} from 'modules/search/selectors'
import {setIsStickyOtaMode} from 'modules/search/slice'

import {isOtaModeProfile} from '@vio/core/src/profile'

import {
  clearStickyOtaMode,
  hasStickyOtaMode,
  setStickyOtaMode
} from './sessionStore'

/**
 * Mounted once at the app shell. Responsible for two related effects:
 *
 *  1. **Bootstrap:** When the URL carries `profile=23afbcc5e3` and the user
 *     isn't already sticky, write the sticky flag (sessionStorage + cookie)
 *     and promote the Redux flag.
 *
 *  2. **Reconciliation:** sessionStorage is the client-side source of truth.
 *     The SSR preload, on the other hand, can only see the cookie — which is
 *     shared across all tabs in the same browser session. If Redux says
 *     sticky=true (because SSR read a stale cookie) but this tab's
 *     sessionStorage is empty, we're in a freshly-opened tab that should
 *     start non-sticky. Clear the cookie and reset Redux.
 *
 *  SAPI requests do NOT depend on this reconciliation completing — they read
 *  sessionStorage directly at dispatch time (see useSyncStoreWithUrl). This
 *  effect only flips the chrome state.
 */
export const useOtaModeBootstrap = (): void => {
  const profile = useSelector(getProfileUrlParam)
  const sticky = useSelector(getIsStickyOtaMode)
  const dispatch = useDispatch()

  useEffect(() => {
    const sessionHasSticky = hasStickyOtaMode()

    if (isOtaModeProfile(profile) && !sessionHasSticky) {
      setStickyOtaMode()
      dispatch(setIsStickyOtaMode(true))
      return
    }

    if (sticky === sessionHasSticky) return

    if (!sessionHasSticky) clearStickyOtaMode()
    dispatch(setIsStickyOtaMode(sessionHasSticky))
  }, [profile, sticky, dispatch])
}
